import { atom, type Atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Checkbox,
  Column,
  Dropdown,
  FormGroup,
  FlexGrid as Grid,
  NumberInput,
  Row,
  Search,
  SkeletonPlaceholder,
  SkeletonText,
  Tag,
} from '@carbon/react';
import type {
  DocumentResponse,
  ReferenceLocationResponse,
} from 'types/api-responses';
import ErrorToast from 'components/error-toast';
import { Documents } from './documents';
import style from './documents-search.module.scss';
import { formatNumber } from '../search/common/format-helpers';
import type { DocumentSearchParams } from './types';
import {
  defaultPayload,
  referenceLocationStyle,
  type ReferenceLocationStyleFn,
} from './utils';
import { debounce } from 'lodash-es';
import {
  searchAtom,
  resetSearchDataAtom,
  documentSearchDateSortSettingAtom,
  formattedDateRangeAtom,
} from './atoms';
import {
  areReferenceLocationBucketsEqual,
  onReferenceLocationClick,
  useCalculateMapDimensions,
} from 'components/map/utils';
import {
  createAbortableLocationsAtom,
  getMapDefaultConfig,
} from 'components/map/atoms';
import { Map, PolygonArea } from 'components/map';
import type { MapLayerMouseEvent } from 'mapbox-gl';
import type { LocationAtom } from 'components/map/types';
import { DownloadButton } from 'components/download-button';
import { useDownloadResource } from 'lib/hooks/useDownloadFile';
import classNames from 'classnames';
import { SearchTips } from './search-tips';
import { atomWithStorage } from 'jotai/utils';
import { sortFields, type SortField } from './sort-fields';
import { ProvisionsFilter } from 'components/provision-filter';
import { CreatedAtFilter } from 'components/created-at-filter/created-at-filter';
import { DocumentViewer } from './document-viewer';
import { isValidYear } from 'utils/dates';

/**
 * Filters by document type from the search's type facet.
 * @return {JSX.Element | null} The filter, or nothing before the facet loads.
 */
function DocumentTypeFilter() {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const facet = searchResult?.data?.facets?.documentType;
  const currentValue = searchResult.currentPayload?.documentType;

  if (!facet?.buckets?.length) return null;

  const allItem = { id: 'all', text: 'All Types' };
  const items = [
    allItem,
    ...facet.buckets.map((bucket) => ({
      id: bucket.key,
      text: `${bucket.label} (${formatNumber(bucket.count)})`,
    })),
  ];

  return (
    <FormGroup legendText="Document Type">
      <Dropdown
        id="document-type-filter"
        titleText=""
        label=""
        items={items}
        itemToString={(item) => item?.text || ''}
        selectedItem={items.find((i) => i.id === currentValue) || allItem}
        onChange={({ selectedItem }) => {
          setSearchPayload((current) => ({
            ...current,
            page: 1,
            documentType:
              selectedItem?.id === 'all' ? undefined : selectedItem?.id,
          }));
        }}
      />
    </FormGroup>
  );
}

/**
 * Filters by provisioning status.
 * @return {JSX.Element} The filter.
 */
function ProvisionFilterGroup() {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);

  const hasCompletion = searchResult.currentPayload?.hasCompletion;

  return (
    <FormGroup legendText="Provisioning Status">
      <ProvisionsFilter
        hasCompletion={hasCompletion}
        onChange={(hasCompletion) =>
          setSearchPayload((current) => ({
            ...current,
            page: 1,
            hasCompletion,
          }))
        }
      />
    </FormGroup>
  );
}

/**
 * Sorts the results by one of the document's dates.
 * @return {JSX.Element} The sort control.
 */
function SortByDate() {
  const setSearchPayload = useSetAtom(searchAtom);
  const [sortSettings, setSortSettings] = useAtom(
    documentSearchDateSortSettingAtom
  );

  const selectedSort = sortFields.find(
    (el) =>
      el.id === sortSettings?.sortAttribute &&
      el.direction === sortSettings?.sortDirection
  );

  return (
    <FormGroup legendText="Sort By Date">
      <Dropdown
        id="sort-documents-by-date"
        titleText=""
        label=""
        items={sortFields}
        itemToString={(item) => (item ? item.text : '')}
        selectedItem={selectedSort ?? null}
        onChange={({ selectedItem }: { selectedItem: SortField }) => {
          setSortSettings({
            sortAttribute: selectedItem.id,
            sortDirection: selectedItem.direction,
          });
          setSearchPayload(
            (current) =>
              current && {
                ...current,
                sortAttribute: selectedItem.id,
                sortDirection: selectedItem.direction,
              }
          );
        }}
      />
    </FormGroup>
  );
}

/**
 * The selected reference locations, each removable.
 * @return {JSX.Element} The pills.
 */
function ReferenceLocationPills() {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const searchPayload = searchResult.currentPayload;

  const facet = searchResult?.data?.facets?.referenceLocation;

  const selectedReferenceLocationIds =
    searchPayload?.referenceLocationIds || [];

  const selectedReferenceLocations =
    facet?.buckets?.filter((referenceLocation) => {
      return selectedReferenceLocationIds.includes(referenceLocation.id);
    }) || [];

  return (
    <FormGroup legendText="Area on Map">
      {selectedReferenceLocations.length ? (
        selectedReferenceLocations.map((referenceLocation) => (
          <Tag
            filter
            key={referenceLocation.id}
            type="purple"
            onClose={() => {
              setSearchPayload((current) => ({
                ...current,
                referenceLocationIds: current?.referenceLocationIds?.filter(
                  (id) => id !== referenceLocation.id
                ),
              }));
            }}
          >
            {`${referenceLocation.shortName} (${referenceLocation.count})`}
          </Tag>
        ))
      ) : (
        <Tag type="blue">All Areas</Tag>
      )}
    </FormGroup>
  );
}

/**
 * Filters by a range of instrument-date years.
 * @return {JSX.Element} The year range.
 */
export function InstrumentDateFacets() {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const searchPayload = searchResult.currentPayload;
  return (
    <FormGroup legendText="Year Range">
      <Grid className="bx--no-gutter" fullWidth>
        <Row>
          <Column>
            <NumberInput
              id="documents-search-instrument-date-min-year"
              label="Start Year"
              allowEmpty={true}
              placeholder="yyyy"
              value={searchPayload?.instrumentDateMinYear || ''}
              className={style.dateField}
              hideSteppers
              onChange={(_event, { value }) => {
                if (value !== '' && !isValidYear(value)) return;
                setSearchPayload((current) => ({
                  ...current,
                  page: 1,
                  instrumentDateMinYear:
                    value === '' ? undefined : Number(value),
                }));
              }}
            />
          </Column>
          <Column>
            <NumberInput
              id="documents-search-instrument-date-max-year"
              label="End Year"
              allowEmpty={true}
              placeholder="yyyy"
              value={searchPayload?.instrumentDateMaxYear || ''}
              className={style.dateField}
              hideSteppers
              onChange={(_event, { value }) => {
                if (value !== '' && !isValidYear(value)) return;
                setSearchPayload((current) => ({
                  ...current,
                  page: 1,
                  instrumentDateMaxYear:
                    value === '' ? undefined : Number(value),
                }));
              }}
            />
          </Column>
        </Row>
      </Grid>
    </FormGroup>
  );
}

/**
 * The county filter with one county toggled.
 * @param {number} countyId The county toggled.
 * @param {DocumentSearchParams} [currentPayload] The applied search.
 * @return {number[]} The county ids now filtered by.
 */
const updatedCounties = (
  countyId: number,
  currentPayload?: DocumentSearchParams
) => {
  const existingCountyIds = currentPayload?.countyIds || [];

  return existingCountyIds.includes(countyId)
    ? existingCountyIds.filter((id) => id !== countyId)
    : existingCountyIds.concat([countyId]);
};

/**
 * Filters by county from the search's county facet.
 * @return {JSX.Element} The county checkboxes.
 */
function CountyCheckboxes() {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const searchPayload = searchResult.currentPayload;
  const facet = searchResult?.data?.facets?.county;
  return (
    <FormGroup legendText="County">
      {facet ? (
        facet.buckets.map((bucket) => (
          <Checkbox
            key={bucket.id}
            id={`search-county-${bucket.id}`}
            labelText={`${bucket.name} (${formatNumber(bucket.count)})`}
            checked={searchPayload?.countyIds?.includes(bucket.id) ?? false}
            onChange={() => {
              setSearchPayload((current) => ({
                ...current,
                page: 1,
                countyIds: updatedCounties(bucket.id, current),
              }));
            }}
          />
        ))
      ) : (
        <Checkbox
          id={`search-county-none`}
          labelText="None"
          disabled
          checked={false}
        />
      )}
    </FormGroup>
  );
}

const mapConfigAtom = atomWithStorage(
  'insights-document-search-map-config',
  getMapDefaultConfig({
    wellsV2: {
      checked: false,
      options: {
        surfaceHole: false,
        bottomHole: false,
        stick: false,
        survey: false,
        vertical: false,
        horizontal: false,
        horizontalPermit: false,
        horizontalDuc: false,
        horizontalProducing: false,
        horizontalShutIn: false,
        horizontalExpiredPermit: false,
        horizontalInactive: false,
        horizontalPrePermit: false,
        horizontalApd: false,
        horizontalProposal: false,
        horizontalPlanned: false,
        verticalPermit: false,
        verticalDuc: false,
        verticalProducing: false,
        verticalShutIn: false,
        verticalExpiredPermit: false,
        verticalInactive: false,
        verticalPrePermit: false,
        verticalApd: false,
        verticalProposal: false,
        verticalPlanned: false,
        permit: false,
        duc: false,
        producing: false,
        shutIn: false,
        expiredPermit: false,
        inactive: false,
        prePermit: false,
        apd: false,
        proposal: false,
        planned: false,
      },
    },
  })
);

/**
 * The map of the results' reference locations; clicking one filters by it.
 * @return {JSX.Element} The map.
 */
function MapSelection() {
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const searchPayload = searchResult.currentPayload;

  const newReferenceLocationBuckets =
    searchResult?.data?.facets?.referenceLocation?.buckets || [];

  const [currentReferenceLocationBuckets, setCurrentReferenceLocationBuckets] =
    useState(newReferenceLocationBuckets);

  if (
    !areReferenceLocationBucketsEqual(
      currentReferenceLocationBuckets,
      newReferenceLocationBuckets
    )
  ) {
    setCurrentReferenceLocationBuckets(newReferenceLocationBuckets);
  }

  const locationsAtom = useMemo(
    () =>
      atom(() => {
        return createAbortableLocationsAtom(currentReferenceLocationBuckets);
      }),
    [currentReferenceLocationBuckets]
  );

  const onClick = useCallback(
    (e: MapLayerMouseEvent) =>
      onReferenceLocationClick(e, searchPayload || {}, setSearchPayload),
    [searchPayload, setSearchPayload]
  );

  const locationCollectionAtom = useAtomValue(useAtomValue(locationsAtom));

  const containerRef = useRef(null);

  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: searchResult?.data?.boundingBox?.points,
    containerRef,
  });

  const facet = searchResult?.data?.facets?.referenceLocation;
  const hoveredReferenceLocations =
    facet?.buckets.filter((referenceLocation) => ({
      id: referenceLocation.id,
      name: referenceLocation.name,
    })) || [];

  const styleFn = useMemo(() => {
    const referenceLocations = facet?.buckets || [];
    let rangeOfCounts = [0, 0];
    if (referenceLocations.length) {
      rangeOfCounts = [
        referenceLocations[referenceLocations.length - 1].count,
        referenceLocations[0].count,
      ];
    }
    const [min, max] = rangeOfCounts;
    /**
     * Shades a location by its document count within the result's range.
     * @param {object} location The location, with its document count.
     * @return {object} Its polygon style.
     */
    const styleFn = (location: ReferenceLocationResponse & { count: number }) =>
      referenceLocationStyle(
        location,
        searchResult?.currentPayload?.referenceLocationIds || [],
        min,
        max - min
      );
    return styleFn;
  }, [facet?.buckets, searchResult?.currentPayload?.referenceLocationIds]);

  return (
    <div className={style.mapArea} ref={containerRef}>
      <Map
        latitude={latitude}
        longitude={longitude}
        zoom={zoom}
        onClick={onClick}
        geographies={hoveredReferenceLocations}
        mapConfigAtom={mapConfigAtom}
        showControls
        showMapFilters
      >
        {locationCollectionAtom.map((locationAtom) => (
          <Suspense fallback={null} key={`${locationAtom}`}>
            <AsyncPolygon locationAtom={locationAtom} styleFn={styleFn} />
          </Suspense>
        ))}
      </Map>
    </div>
  );
}

/**
 * One reference location's polygon, drawn once its geometry loads.
 * @param {object} props The polygon.
 * @param {LocationAtom} props.locationAtom Loads the location.
 * @param {ReferenceLocationStyleFn} props.styleFn Styles it.
 * @return {JSX.Element | null} The polygon, or nothing until it loads.
 */
function AsyncPolygon({
  locationAtom,
  styleFn,
}: {
  locationAtom: LocationAtom;
  styleFn: ReferenceLocationStyleFn;
}) {
  const location = useAtomValue(locationAtom);
  if (!location || 'error' in location) return null;
  return (
    <PolygonArea
      geometry={location.geometry}
      id={location.id}
      style={styleFn(location)}
    />
  );
}

const selectedDocumentIdAtom = atom<number | undefined>(undefined);

interface Props {
  onAddDocument?: (doc: DocumentResponse) => void;
  usedDocumentsAtom?: Atom<number[]>;
  noGutter?: boolean;
  showTips?: boolean;
  withinReferenceLocationId?: number;
  vestingOnly?: boolean;
  vestingTractIds?: string[];
  downloadHref?: string;
  hideMap?: boolean;
}

/**
 * The documents search: text search, filters, map, results and preview.
 * @param {Props} props The search's options.
 * @return {JSX.Element} The search.
 */
function DocumentsSearcher({
  onAddDocument,
  usedDocumentsAtom = atom<number[]>([]),
  noGutter,
  showTips,
  withinReferenceLocationId,
  vestingOnly,
  vestingTractIds,
  downloadHref,
  hideMap,
}: Props) {
  const [selectedDocumentId, setSelectedDocumentId] = useAtom(
    selectedDocumentIdAtom
  );
  const [searchDisplayText, setSearchDisplayText] = useState('');
  const [searchResult, setSearchPayload] = useAtom(searchAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);
  const searchSettings = useAtomValue(documentSearchDateSortSettingAtom);

  useEffect(() => {
    return () => resetSearchData({ unsetCurrentPayload: true });
  }, [resetSearchData]);

  // The ids by value, so a re-rendered parent's new array doesn't re-search.
  const vestingTractIdsKey = JSON.stringify(vestingTractIds);
  const debouncedSetSearchPayload = useMemo(
    () =>
      debounce((value?: string) => {
        resetSearchData(undefined);
        setSearchPayload((current) => ({
          ...current,
          pageSize: current?.pageSize || 100,
          page: 1,
          // A changed filter prop re-searches without clearing the typed text.
          text: value ?? current?.text ?? '',
          withinReferenceLocationId,
          vestingOnly,
          vestingTractIds,
          sortAttribute: searchSettings
            ? searchSettings.sortAttribute
            : undefined,
          sortDirection: searchSettings
            ? searchSettings.sortDirection
            : undefined,
        }));
      }, 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setSearchPayload,
      resetSearchData,
      withinReferenceLocationId,
      vestingOnly,
      vestingTractIdsKey,
    ]
  );

  useEffect(() => {
    debouncedSetSearchPayload();
  }, [debouncedSetSearchPayload]);

  /**
   * Opens a document in the preview, or closes it.
   * @param {number | undefined} documentId The document, or none.
   */
  const handleViewDocument = (documentId: number | undefined) => {
    setSelectedDocumentId(documentId);
  };
  const selectedDocument = searchResult?.data?.results.find(
    (doc) => doc.id === selectedDocumentId
  );

  const [formattedDateRange] = useAtom(formattedDateRangeAtom);
  return (
    <>
      {searchResult?.error ? <ErrorToast message={searchResult.error} /> : null}
      <Grid className={classNames({ 'bx--no-gutter': noGutter })} fullWidth>
        <Row>
          <Column sm={12} md={2} lg={3}>
            <div className={style.countHeading}>
              <h2>
                {formatNumber(searchResult.data?.totalCount || 0)} Documents
              </h2>
            </div>
          </Column>
          <Column sm={12} md={10} lg={13}>
            <div className={style.searchWrapper}>
              <Search
                data-modal-primary-focus
                labelText="Search"
                placeholder="Search"
                size="lg"
                {...{ autoFocus: true }}
                value={searchDisplayText}
                onChange={({ target: { value } }) => {
                  setSearchDisplayText(value);
                  debouncedSetSearchPayload(value);
                }}
                className={style.input}
              />
              {showTips && <SearchTips />}
              {withinReferenceLocationId && downloadHref && (
                <DownloadList downloadHref={downloadHref} />
              )}
            </div>
          </Column>
        </Row>

        <Row>
          <Column sm={12} md={2} lg={3} className={style.documentFilters}>
            {/* Holds the filter column's shape until the facets land. */}
            {!searchResult.data?.facets && searchResult.loading && (
              <div className={style.filtersSkeleton}>
                {[...Array(5).keys()].map((index) => (
                  <div key={index}>
                    <SkeletonText width="40%" />
                    <SkeletonPlaceholder className={style.fieldSkeleton} />
                  </div>
                ))}
              </div>
            )}
            {searchResult.data?.facets && (
              <>
                <ResetFilters />
                <DocumentTypeFilter />
                <ProvisionFilterGroup />
                <SortByDate />
                <CreatedAtFilter
                  formattedDateRange={formattedDateRange}
                  onChange={(dateRange: any) => {
                    if (dateRange) {
                      setSearchPayload((current) => ({
                        ...current,
                        created_at: dateRange,
                      }));
                    }
                  }}
                />
                <InstrumentDateFacets />
                <CountyCheckboxes />
                {!hideMap && <ReferenceLocationPills />}
              </>
            )}
          </Column>
          <Column sm={12} md={6} lg={13}>
            <div
              className={classNames(style.mainContent, {
                [style.withViewer]: !!selectedDocumentId,
              })}
            >
              {!hideMap && searchResult.data?.results.length ? (
                <MapSelection />
              ) : null}
              <Documents
                searchResult={searchResult.data}
                onChange={(newSearch) =>
                  setSearchPayload((current) => ({
                    ...current,
                    ...newSearch,
                  }))
                }
                onAddDocument={onAddDocument}
                usedDocumentsAtom={usedDocumentsAtom}
                onViewDocument={handleViewDocument}
                loading={searchResult.loading}
                selectedDocumentId={selectedDocumentId}
                setSelectedDocumentId={setSelectedDocumentId}
              />
              {selectedDocumentId && (
                <div className={style.viewerContainer}>
                  <DocumentViewer
                    documentId={selectedDocumentId}
                    documentName={selectedDocument?.documentTitle}
                    onClose={() => handleViewDocument(undefined)}
                  />
                </div>
              )}
            </div>
          </Column>
        </Row>
      </Grid>
    </>
  );
}

/**
 * Downloads the reference location's document list.
 * @param {object} props The download.
 * @param {string} props.downloadHref The list's download resource.
 * @return {JSX.Element} The download button.
 */
function DownloadList({ downloadHref }: { downloadHref: string }) {
  // Reference location documents download — only rendered when
  // withinReferenceLocationId is provided. Uses the static downloadHref from
  // the reference location resource; user-applied search filters (text,
  // date range, county, etc.) are not reflected in the exported file.
  const download = useDownloadResource(downloadHref);
  return (
    <DownloadButton
      loading={download.generatingDownload}
      loadingText="Building List"
      kind="primary"
      className={style.downloadList}
      onClick={() => download.triggerDownload()}
    >
      Download List
    </DownloadButton>
  );
}

/**
 * Clears every filter and sort, keeping the text and the page's own scope.
 * @return {JSX.Element} The reset button.
 */
function ResetFilters() {
  const setSearchPayload = useSetAtom(searchAtom);
  const setSortSettings = useSetAtom(documentSearchDateSortSettingAtom);
  const resetSearchData = useSetAtom(resetSearchDataAtom);
  const setDateRange = useSetAtom(formattedDateRangeAtom);
  return (
    <div className={style.resetFilters}>
      <Button
        kind="ghost"
        size="md"
        onClick={() => {
          setSortSettings(undefined);
          resetSearchData(undefined);
          setDateRange(undefined);
          setSearchPayload((current) => ({
            ...defaultPayload,
            text: current?.text || '',
            withinReferenceLocationId: current?.withinReferenceLocationId,
            vestingOnly: current?.vestingOnly,
            vestingTractIds: current?.vestingTractIds,
          }));
        }}
      >
        Reset Filters
      </Button>
    </div>
  );
}

/**
 * The documents search page's content.
 * @param {Props} props The search's options.
 * @return {JSX.Element} The search.
 */
function DocumentsSearch(props: Props) {
  return <DocumentsSearcher {...props} />;
}

export { DocumentsSearch };
